import standardRangeCatalogJson from "./comparison_standard_ranges.json";

import type { TeacherWebCompData, TeacherWebCompResults } from "../../api/teacherWeb";

export type StandardnessDimension =
  | "start"
  | "impact"
  | "followThrough"
  | "power"
  | "stability"
  | "technique";

export type StandardnessDimensionScore = {
  dimension: StandardnessDimension;
  value: number | null;
};

export type StandardnessScore = {
  displayValue: number;
  dimensions: StandardnessDimensionScore[];
  value: number;
};

type StandardMetricRange = {
  mean: number;
  std: number;
};

type StandardKineticChainRange = {
  forearm_speed: StandardMetricRange;
  hip_speed: StandardMetricRange;
  shoulder_speed: StandardMetricRange;
};

type StandardFunnyStatsRange = {
  impact_angle: StandardMetricRange;
  impact_height: StandardMetricRange;
  impact_speed: StandardMetricRange;
};

type StandardTechniqueRange = {
  end_frame: Record<string, StandardMetricRange>;
  funny_stats?: StandardFunnyStatsRange;
  impact_frame: Record<string, StandardMetricRange>;
  kinetic_chain?: StandardKineticChainRange;
  name: string;
  start_frame: Record<string, StandardMetricRange>;
};

type MetricName =
  | "elbowAngle"
  | "shoulderHipAngle"
  | "shoulderArmAngle"
  | "shoulderRotation"
  | "hipAngle"
  | "hipRotation"
  | "kneeAngle"
  | "trunkFlexion"
  | "trunkLateralTilt"
  | "trunkRotation"
  | "centerOfGravity";

type MetricDefinition = {
  baseWeight: number;
  excludesImpact: boolean;
  key: string;
  name: MetricName;
  usesCircularDifference: boolean;
};

type EventName = "start" | "impact" | "end";
type KineticJoint = "hip" | "shoulder" | "forearm";
type PlayingSide = "left" | "right";

type KineticChainScore = {
  deviation: number;
  score: number;
};

const REQUIRED_SAMPLE_COUNT = 31;
const STANDARD_INTERVAL_SIGMA_RADIUS = 2;
const ROBUST_LOSS_THRESHOLD = 2;
const KEY_FRAME_SCORE_WEIGHT = 0.9;
const KINETIC_CHAIN_SCORE_WEIGHT = 0.1;
const POWER_KINETIC_CHAIN_WEIGHT = 0.1;
const POWER_IMPACT_SPEED_WEIGHT = 0.9;
const KINETIC_CHAIN_SEQUENCE_DEVIATION_PER_INVERSION = 0.5;
const LOGISTIC_SLOPE = Math.log(3);
const LOGISTIC_AMPLITUDE = 120;
const MINIMUM_DISPLAY_SCORE = 40;
const MAXIMUM_DISPLAY_SCORE = 100;
const MAXIMUM_LOGISTIC_EXPONENT = 60;

const eventDefinitions: Array<{
  frameOffset: number;
  name: EventName;
  scoreWeight: number;
}> = [
  { frameOffset: 0, name: "start", scoreWeight: 0.25 },
  { frameOffset: 1, name: "impact", scoreWeight: 0.5 },
  { frameOffset: 2, name: "end", scoreWeight: 0.25 },
];

const metricDefinitions: MetricDefinition[] = [
  metric("elbowAngle", "elbow_angle", "upperBody"),
  metric("shoulderHipAngle", "shoulder_hip_angle", "upperBody"),
  metric("shoulderArmAngle", "shoulder_arm_angle", "upperBody"),
  metric("shoulderRotation", "shoulder_rotation", "rotationCoordination", true, true),
  metric("hipAngle", "hip_angle", "lowerBodyBalance"),
  metric("hipRotation", "hip_rotation", "rotationCoordination", true, true),
  metric("kneeAngle", "knee_angle", "lowerBodyBalance"),
  metric("trunkFlexion", "trunk_flexion_angle", "posture"),
  metric("trunkLateralTilt", "trunk_lateral_tilt_angle", "posture"),
  metric("trunkRotation", "trunk_rotation_angle", "rotationCoordination", true),
  metric("centerOfGravity", "center_of_gravity", "lowerBodyBalance"),
];

const standardRangeCatalog = standardRangeCatalogJson as Record<string, StandardTechniqueRange>;

export function calculateComparisonStandardness(
  result: TeacherWebCompResults | undefined,
): StandardnessScore | null {
  const techType = result?.tech_type?.trim().toLowerCase();
  const user = result?.data?.[0];
  if (!techType || !user) return null;

  const side = playingSide(techType);
  const standardRanges = standardRangeCatalog[techType];
  if (!side || !standardRanges) return null;

  const userKeyFrames = validKeyFrames(user.key_frames) ? user.key_frames : null;
  if (!userKeyFrames) return null;

  const metricValues = new Map<MetricName, number[]>();
  for (const definition of metricDefinitions) {
    const values = metricUserValues(user, definition, side);
    if (!values) return null;
    metricValues.set(definition.name, values);
  }

  const eventScores = new Map<EventName, number>();
  const eventDeviations = new Map<EventName, number>();

  for (const event of eventDefinitions) {
    const ranges = rangesForEvent(standardRanges, event.name);
    const frameIndex = userKeyFrames[event.frameOffset];
    let weightedLoss = 0;
    let totalWeight = 0;

    for (const definition of metricDefinitions) {
      if (event.name === "impact" && definition.excludesImpact) continue;

      const values = metricValues.get(definition.name);
      const range = ranges[definition.key];
      if (!values || !range) return null;

      const loss = standardizedExcessLoss(
        values[frameIndex],
        range,
        definition.usesCircularDifference,
      );
      if (loss === null) return null;
      weightedLoss += loss * definition.baseWeight;
      totalWeight += definition.baseWeight;
    }

    if (!(totalWeight > 0) || !Number.isFinite(weightedLoss)) return null;
    const deviation = Math.sqrt(weightedLoss / totalWeight);
    eventDeviations.set(event.name, deviation);
    eventScores.set(event.name, mappedScore(deviation));
  }

  let weightedScore = 0;
  for (const event of eventDefinitions) {
    const score = eventScores.get(event.name);
    if (score === undefined) return null;
    weightedScore += event.scoreWeight * score;
  }

  let kineticChainScore: KineticChainScore | null = null;
  if (standardRanges.kinetic_chain) {
    kineticChainScore = calculateKineticChainScore(
      user,
      side,
      standardRanges.kinetic_chain,
    );
    if (!kineticChainScore) return null;
  }

  const finalScore = kineticChainScore
    ? KEY_FRAME_SCORE_WEIGHT * weightedScore
      + KINETIC_CHAIN_SCORE_WEIGHT * kineticChainScore.score
    : weightedScore;
  if (!Number.isFinite(finalScore)) return null;

  const powerScore = calculatePowerScore(
    user,
    kineticChainScore,
    standardRanges.funny_stats?.impact_speed,
  );
  const stabilityScore = calculateStabilityScore(user, result?.data?.[1]);

  return {
    displayValue: Math.round(finalScore),
    dimensions: [
      { dimension: "start", value: eventScores.get("start") ?? null },
      { dimension: "impact", value: eventScores.get("impact") ?? null },
      { dimension: "followThrough", value: eventScores.get("end") ?? null },
      { dimension: "power", value: powerScore },
      { dimension: "stability", value: stabilityScore },
      { dimension: "technique", value: finalScore },
    ],
    value: finalScore,
  };
}

function metric(
  name: MetricName,
  key: string,
  domain: "upperBody" | "rotationCoordination" | "lowerBodyBalance" | "posture",
  usesCircularDifference = false,
  excludesImpact = false,
): MetricDefinition {
  const domainMetricCount = domain === "posture" ? 2 : 3;
  return {
    baseWeight: 0.25 / domainMetricCount,
    excludesImpact,
    key,
    name,
    usesCircularDifference,
  };
}

function playingSide(techType: string): PlayingSide | null {
  if (techType.startsWith("left_")) return "left";
  if (techType.startsWith("right_")) return "right";
  return null;
}

function metricUserValues(
  data: TeacherWebCompData,
  definition: MetricDefinition,
  side: PlayingSide,
): number[] | null {
  const stats = data.frame_stats;
  if (!stats) return null;

  const sidedMetrics = new Set<MetricName>([
    "elbowAngle",
    "shoulderHipAngle",
    "shoulderArmAngle",
    "shoulderRotation",
    "hipAngle",
    "hipRotation",
    "kneeAngle",
  ]);
  const statsKey = sidedMetrics.has(definition.name)
    ? `${definition.key}_${side}`
    : definition.key;
  return firstSeries(stats[statsKey]);
}

function firstSeries(value: unknown): number[] | null {
  if (!Array.isArray(value) || !Array.isArray(value[0])) return null;
  const values = value[0];
  return validSeries(values) ? values : null;
}

function validSeries(value: unknown): value is number[] {
  return Array.isArray(value)
    && value.length === REQUIRED_SAMPLE_COUNT
    && value.every((item) => typeof item === "number" && Number.isFinite(item));
}

function validKeyFrames(value: unknown): value is [number, number, number] {
  if (!Array.isArray(value) || value.length !== 3) return false;
  if (!value.every((frame) => Number.isInteger(frame))) return false;
  const [start, impact, end] = value;
  return start >= 0 && start <= impact && impact <= end && end < REQUIRED_SAMPLE_COUNT;
}

function rangesForEvent(
  ranges: StandardTechniqueRange,
  event: EventName,
): Record<string, StandardMetricRange> {
  if (event === "start") return ranges.start_frame;
  if (event === "impact") return ranges.impact_frame;
  return ranges.end_frame;
}

function standardizedExcessLoss(
  userValue: number,
  standardRange: StandardMetricRange,
  circular: boolean,
): number | null {
  const { mean, std } = standardRange;
  if (![userValue, mean, std].every(Number.isFinite) || !(std > 0)) return null;

  const difference = userValue - mean;
  const distance = circular ? Math.abs(wrappedAngle(difference)) : Math.abs(difference);
  const intervalRadius = STANDARD_INTERVAL_SIGMA_RADIUS * std;
  const excess = Math.max(0, distance - intervalRadius);
  const standardizedExcess = excess / std;
  return Number.isFinite(standardizedExcess) ? robustLoss(standardizedExcess) : null;
}

function calculateKineticChainScore(
  data: TeacherWebCompData,
  side: PlayingSide,
  ranges: StandardKineticChainRange,
): KineticChainScore | null {
  const rawSummary = side === "left"
    ? data.kinetic_chain_summary_left
    : data.kinetic_chain_summary_right;
  const summary = asRecord(rawSummary);
  const peakTimes = asRecord(summary?.peak_times);
  const peakSpeeds = asRecord(summary?.peak_speeds);
  if (!peakTimes || !peakSpeeds) return null;

  const joints: KineticJoint[] = ["hip", "shoulder", "forearm"];
  const losses: number[] = [];
  for (const joint of joints) {
    const peakTime = finiteNumber(peakTimes[joint]);
    const peakSpeed = finiteNumber(peakSpeeds[joint]);
    const speedRange = ranges[`${joint}_speed` as keyof StandardKineticChainRange];
    if (peakTime === null || peakSpeed === null || peakTime < 0 || peakSpeed < 0 || speedRange.mean < 0) {
      return null;
    }
    const loss = standardizedExcessLoss(peakSpeed, speedRange, false);
    if (loss === null) return null;
    losses.push(loss);
  }

  let inversionCount = 0;
  for (let earlier = 0; earlier < joints.length; earlier += 1) {
    for (let later = earlier + 1; later < joints.length; later += 1) {
      const earlierTime = finiteNumber(peakTimes[joints[earlier]]);
      const laterTime = finiteNumber(peakTimes[joints[later]]);
      if (earlierTime === null || laterTime === null) return null;
      if (earlierTime > laterTime) inversionCount += 1;
    }
  }

  losses.push(robustLoss(inversionCount * KINETIC_CHAIN_SEQUENCE_DEVIATION_PER_INVERSION));
  const meanLoss = losses.reduce((total, value) => total + value, 0) / losses.length;
  if (!(meanLoss >= 0) || !Number.isFinite(meanLoss)) return null;
  const deviation = Math.sqrt(meanLoss);
  return { deviation, score: mappedScore(deviation) };
}

function calculatePowerScore(
  data: TeacherWebCompData,
  kineticChainScore: KineticChainScore | null,
  impactSpeedRange: StandardMetricRange | undefined,
): number | null {
  const impactSpeed = finiteNumber(asRecord(data.funny_stats)?.impact_speed);
  if (impactSpeed === null || !impactSpeedRange) return null;
  const impactSpeedLoss = standardizedExcessLoss(impactSpeed, impactSpeedRange, false);
  if (impactSpeedLoss === null) return null;

  const combinedLoss = kineticChainScore
    ? POWER_IMPACT_SPEED_WEIGHT * impactSpeedLoss
      + POWER_KINETIC_CHAIN_WEIGHT * kineticChainScore.deviation ** 2
    : impactSpeedLoss;
  return combinedLoss >= 0 && Number.isFinite(combinedLoss)
    ? mappedScore(Math.sqrt(combinedLoss))
    : null;
}

function calculateStabilityScore(
  user: TeacherWebCompData,
  reference: TeacherWebCompData | undefined,
): number | null {
  const userRows = user.frame_stats?.center_of_gravity;
  const referenceRows = reference?.frame_stats?.center_of_gravity;
  if (!Array.isArray(userRows) || !Array.isArray(referenceRows)) return null;

  const userValues = validSeries(userRows[0]) ? userRows[0] : null;
  const standardMeans = validSeries(referenceRows[1]) ? referenceRows[1] : null;
  const standardDeviations = validSeries(referenceRows[2]) ? referenceRows[2] : null;
  if (!userValues || !standardMeans || !standardDeviations || !standardDeviations.every((value) => value > 0)) {
    return null;
  }

  let lossTotal = 0;
  for (let index = 0; index < REQUIRED_SAMPLE_COUNT; index += 1) {
    const loss = standardizedExcessLoss(
      userValues[index],
      { mean: standardMeans[index], std: standardDeviations[index] },
      false,
    );
    if (loss === null) return null;
    lossTotal += loss;
  }
  return mappedScore(Math.sqrt(lossTotal / REQUIRED_SAMPLE_COUNT));
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" && !Array.isArray(value)
    ? value as Record<string, unknown>
    : null;
}

function finiteNumber(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function wrappedAngle(value: number): number {
  let wrapped = value % 360;
  if (wrapped >= 180) wrapped -= 360;
  if (wrapped < -180) wrapped += 360;
  return wrapped;
}

function robustLoss(standardizedExcess: number): number {
  if (standardizedExcess <= ROBUST_LOSS_THRESHOLD) {
    return standardizedExcess * standardizedExcess;
  }
  return 2 * ROBUST_LOSS_THRESHOLD * standardizedExcess
    - ROBUST_LOSS_THRESHOLD * ROBUST_LOSS_THRESHOLD;
}

function mappedScore(deviation: number): number {
  const exponent = Math.min(
    Math.max(LOGISTIC_SLOPE * deviation, 0),
    MAXIMUM_LOGISTIC_EXPONENT,
  );
  const score = MINIMUM_DISPLAY_SCORE + LOGISTIC_AMPLITUDE / (1 + Math.exp(exponent));
  return Math.min(Math.max(score, MINIMUM_DISPLAY_SCORE), MAXIMUM_DISPLAY_SCORE);
}
